<script>
  import { afterUpdate, onMount } from 'svelte';
  import { format } from 'svelte-i18n';
  import { Dropdown, DropdownMenu, DropdownToggle, Tooltip } from '@sveltestrap/sveltestrap';

  import { Icon } from '@client/components/icon';

  import { QueryParams } from '@client/utils/query-params';

  import { generateUUID } from '@common/utils/strings';

  import FolderItems from './folder-items.svelte';

  export let folders = [];
  export let onClick = () => {};

  let selectedFolders = [];
  let selectedFoldersTooltip = '';
  let folderSelected = false;

  const id = generateUUID();

  /**
   * Retrieves subtree of folders for a given folder id.
   * @param {string} folderUnique - The unique identifier of the selected folder.
   * @param {Object[]} folderTree - The structured tree containing folder objects.
   * @returns {Object[] | null} - Returns an object if the folder is found, otherwise null.
   */
  const findSubFolders = (folderUnique, folderTree) => {
    for (const folder of folderTree) {
      if (folder.unique === folderUnique) {
        return folder;
      }

      if (folder.files && folder.files.length) {
        const childFolders = findSubFolders(folderUnique, folder.files);

        if (childFolders) {
          return childFolders;
        }
      }
    }
    return null;
  };

  /**
   * Retrieves parent id of a folder given a unique identifier.
   * @param {string} folderUnique - The unique identifier of the selected folder.
   * @param {Object[]} folderTree - The structured tree containing folder objects.
   * @returns {string | null} - Returns a string if the id is found, otherwise null.
   */
  const findParentFolder = (folderUnique, folderTree) => {
    for (let folder of folderTree) {
      if (folder.unique === folderUnique) {
        return folder.parent === '#' ? 'Root' : folder.parent;
      }

      if (folder.files && folder.files.length > 0) {
        const result = findParentFolder(folderUnique, folder.files);
        if (result) return result;
      }
    }

    return null;
  };

  /**
   * Retrieves a folder name given a folder id.
   * @param {string} folderId - The folder id of the selected folder.
   * @param {Object[]} folderTree - The structured tree containing folder objects.
   * @returns {string | null} - Returns a string if the name is found, otherwise null.
   */
  const findFolderNameById = (folderId, folderTree) => {
    for (let folder of folderTree) {
      if (folderId === 'Root') {
        return 'Root';
      }

      if (folder.id === folderId) {
        return folder.name;
      }

      if (folder.files && folder.files.length > 0) {
        const result = findFolderNameById(folderId, folder.files);
        if (result) return result;
      }
    }
    return null;
  };

  /**
   * Selects all subfolders of a given folder subtree.
   * @param {Object[]} folder - The folder object to select all subfolders for.
   */
  const selectAllRelatedFolders = (folder) => {
    const selectSubfolders = (folder) => {
      folder.files.forEach((subfolder) => {
        if (!selectedFolders.includes(subfolder.unique)) {
          selectedFolders = [...selectedFolders, subfolder.unique];
        }

        if (subfolder.files && subfolder.files.length) {
          selectSubfolders(subfolder);
        }
      });
    };

    selectSubfolders(folder);
  };

  /**
   * Deselects all subfolders of a given folder subtree.
   * @param {Object[]} folder - The folder object to deselect all subfolders for.
   */
  const deselectAllRelatedFolders = (folder) => {
    const deselectSubfolders = (folder) => {
      folder.files.forEach((subfolder) => {
        if (selectedFolders.includes(subfolder.unique)) {
          selectedFolders = selectedFolders.filter((selected) => selected !== subfolder.unique);
        }

        if (subfolder.files && subfolder.files.length) {
          deselectSubfolders(subfolder);
        }
      });
    };

    deselectSubfolders(folder);
  };

  /**
   * Selects or deselects a folder and its subfolders.
   * @param {Event} e - The event object.
   * @param {string} folderUnique - The unique identifier of the selected folder.
   * @param {boolean} parent - Determines if the folder is a parent folder.
   */
  const onSelectFolder = (e, folderUnique, parent) => {
    const folderIndex = selectedFolders.indexOf(folderUnique);

    if (folderIndex > -1) {
      // unselect current folder
      selectedFolders = selectedFolders.filter((selected) => selected !== folderUnique);

      // if parent folder is being unselected, deselect all its subfolders
      if (parent) {
        const subtree = findSubFolders(folderUnique, folders);

        if (subtree) {
          deselectAllRelatedFolders(subtree);
        }
      }
    } else {
      // select current folder
      selectedFolders = [...selectedFolders, folderUnique];

      // if parent folder is being selected, select all its subfolders
      if (parent) {
        const subtree = findSubFolders(folderUnique, folders);

        if (subtree) {
          selectAllRelatedFolders(subtree);
        }
      }
    }

    selectedFolders = [...new Set(selectedFolders)];
    folderSelected = selectedFolders.length > 0;

    const selectedFolderNames = selectedFolders.map((folder) => folder.split('-')[0]);
    const parentFolders = selectedFolders.map((folder) => findParentFolder(folder, folders));
    const parentFolderNames = parentFolders.map((folder) => findFolderNameById(folder, folders));

    selectedFoldersTooltip = selectedFolderNames
      .map((folder, index) => {
        return `<b>${parentFolderNames[index]}</b> -> ${folder}`;
      })
      .join('<br/>');

    updateQueryParams();
    onClick(e, selectedFolders);
  };

  const updateQueryParams = () => {
    const uniqueFolders = [...new Set(selectedFolders)];
    const selected = uniqueFolders.join(',');

    if (selected.length) {
      QueryParams.set('folders', selected);
    } else {
      QueryParams.delete('folders');
    }
  };

  const initialize = () => {
    const queryParamValue = QueryParams.get('folders');

    if (queryParamValue?.length) {
      selectedFolders = queryParamValue.split(',').map((unique) => unique.trim());
      folderSelected = selectedFolders.length > 0;

      onClick(null, selectedFolders);
    } else {
      QueryParams.delete('folders');
    }
  };

  onMount(initialize);

  afterUpdate(updateQueryParams);
</script>

<Dropdown autoClose="outside">
  <DropdownToggle tag="div">
    <div id="filter-{id}" class="filter-toggle" class:active={folderSelected}>
      <span role="button" tabindex="0">
        <Icon name="folder" class="pointer text-body-secondary" size={15} />
        Folders
        <div class="selected-total">
          {selectedFolders.length}
        </div>
        {#if folderSelected}
          <Tooltip class="folder-filter-tooltip" target="filter-{id}" placement="right">
            <b>{$format('label.SELECTED_FOLDERS')}</b>
            <br />
            <br />
            {@html selectedFoldersTooltip}
          </Tooltip>
        {/if}
      </span>
    </div>
  </DropdownToggle>
  <div class="folder-menu">
    <DropdownMenu class="padded-menu" style="max-height: 400px; overflow: auto;">
      {#each folders as folder}
        <FolderItems bind:folder bind:selectedFolders {onSelectFolder} />
      {/each}
    </DropdownMenu>
  </div>
</Dropdown>

<style lang="scss" src="./folder-filter.scss"></style>
